import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const NotificationSlider = ({ isVisible, onClose, onNotificationsChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!isVisible) return;

      setLoading(true);
      try {
        const token = sessionStorage.getItem("token");
        const response = await axios.get(`${BASE_URL}/api/notification/fetch`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          const sorted = [...response.data.notifications].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setNotifications(sorted);
          onNotificationsChange?.(sorted.length);
        }
      } catch (error) {
        console.error("Error fetching notifications", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [isVisible]);

  const handleDismiss = async (id) => {
    try {
      const token = sessionStorage.getItem("token");
      await axios.post(
        `${BASE_URL}/api/notification/dismiss`,
        { notificationId: id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Optimistically update UI
      const updatedNotifications = notifications.filter((n) => n._id !== id);
      setNotifications(updatedNotifications);
      onNotificationsChange?.(updatedNotifications.length);
    } catch (error) {
      console.error("Error dismissing notification", error);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours > 9) {
      return created.toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    }

    const secondsAgo = Math.floor(diffMs / 1000);
    if (secondsAgo < 60) return `${secondsAgo} sec ago`;
    const minutesAgo = Math.floor(secondsAgo / 60);
    if (minutesAgo < 60) return `${minutesAgo} min ago`;
    const hoursAgo = Math.floor(minutesAgo / 60);
    return `${hoursAgo} hour${hoursAgo > 1 ? "s" : ""} ago`;
  };

  const visibleNotifications = showAll
    ? notifications
    : notifications.slice(0, 3);

  if (!isVisible) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: "fixed",
        bottom: { xs: 80, sm: 80 },
        left: { xs: 24, sm: "auto" },
        right: { xs: "auto", sm: 24 },
        width: { xs: "88vw", sm: 340 },
        maxHeight: "60vh",
        overflow: "auto",
        zIndex: 1300,
        borderRadius: 2,
        p: 1.5,
        backgroundColor: "#fff",
        boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.12)",
        transition: "all 0.3s ease-in-out",
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize="1rem" fontWeight="600">
          Notifications
        </Typography>
        {/* <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton> */}
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* List */}
      <Box
        sx={{
          maxHeight: showAll ? 280 : "auto",
          overflowY: showAll ? "auto" : "visible",
          pr: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {loading ? (
          <Box textAlign="center" py={2}>
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, textAlign: "center", fontSize: "0.8rem" }}
          >
            No notifications
          </Typography>
        ) : (
          <List dense>
            {visibleNotifications.map((n) => (
              <ListItem
                key={n._id}
                alignItems="flex-start"
                disableGutters
                sx={{
                  mb: 0.8,
                  pb: 0.5,
                }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        whiteSpace: "pre-wrap",
                        pr: 1,
                      }}
                    >
                      {n.message}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                    >
                      {formatTimeAgo(n.createdAt)}
                    </Typography>
                  }
                />

                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDismiss(n._id)}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* View All / Show Less */}
      {notifications.length > 3 && (
        <Box textAlign="center" mt={1}>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowAll(!showAll)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              color: "primary.main",
              borderRadius: 1,
              px: 1.5,
              "&:hover": {
                backgroundColor: "#e3f2fd",
                color: "#1565c0",
              },
              "&:active": {
                backgroundColor: "#bbdefb",
                color: "#0d47a1",
              },
            }}
          >
            {showAll ? "Show less" : `View all (${notifications.length})`}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default NotificationSlider;